import { ScanBatch } from '../../types/scan';

/** Human-readable names for each scan type, keyed by the backend `scan_type` value. */
export const SCAN_TYPE_NAMES: Record<string, string> = {
  secrets_scan: 'Secrets Scan',
  semgrep_scan: 'Security Scan',
  deep_ai_scan: 'Deep AI Security Scan',
  sbom_scan: 'SBOM Scan',
};

/**
 * Compact label for a batch that has not produced findings yet, used in the scans
 * table before the first progress poll returns. Mirrors the phases the backend
 * moves a batch through: queued -> creating_scans -> scanning -> assessing.
 */
export const RUNNING_STATUS_LABELS: Record<string, string> = {
  queued: 'Waiting to start…',
  creating_scans: 'Starting scan…',
  assessing: 'Analyzing findings…',
};

export const formatRunningStatus = (status: string): string => RUNNING_STATUS_LABELS[status] || 'Scanning…';

/** Format an ISO timestamp compactly for the scans table, e.g. "Aug 13 · 10:06". */
export const formatDateCompact = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} · ${time}`;
};

/** Format an ISO timestamp without the year, e.g. "Jun 18, 10:06" (24h, as the scans table). */
export const formatScanBatchTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day}, ${time}`;
};

/**
 * Secondary line for a scan in the Findings page scan filter, e.g.
 * "main · Jun 18, 10:06 · 12 findings" — what a batch scanned, when, and what it surfaced.
 */
export const formatScanBatchMeta = (batch: ScanBatch): string => {
  const parts: string[] = [];
  const branches = batch.branches ?? [];
  if (branches.length === 1) parts.push(branches[0]);
  else if (branches.length > 1) parts.push(`${branches.length} branches`);
  parts.push(formatScanBatchTimestamp(batch.created_at));
  const findings = batch.actionable_findings_count;
  if (findings !== undefined && findings !== null) {
    parts.push(findings === 1 ? '1 finding' : `${findings} findings`);
  }
  return parts.join(' · ');
};
